import axios from "axios";
import { showToast } from "src/components/ToastProvider";
import ToastTypes from "src/enum/ToastTypes";
import { clearAccessToken } from "src/store/slices/authSlice";
import { store } from "src/store/store";

// Store for dynamic shopId
let dynamicShopId = null;

// Function to set the shopId dynamically
export const setShopId = (shopId) => {
  dynamicShopId = shopId;
  // Update the default headers
  api.defaults.headers["Client-Id"] = shopId;
};

// Function to get the current shopId
export const getShopId = () => {
  return dynamicShopId;
};

const api = axios.create({
  baseURL: process.env.REACT_APP_API_HTTPS_URL,
  headers: {
    "Content-Type": "application/json",
    "Client-Id": dynamicShopId,
  },
});

export const clearSession = () => {
  store.dispatch(clearAccessToken());
  localStorage.removeItem("accessToken");
  localStorage.removeItem("userEmail");
  localStorage.removeItem("expiresAt");
};

/**
 * Check if the token has expired
 * @returns {boolean} True if token is expired or invalid
 */
export const isTokenExpired = () => {
  const expiresAt = localStorage.getItem("expiresAt");
  if (!expiresAt) return true;
  
  try {
    // Convert to milliseconds for reliable comparison
    const expirationTime = new Date(expiresAt).getTime();
    const currentTime = new Date().getTime();
    return currentTime >= expirationTime;
  } catch (error) {
    console.error("Error parsing expiration date:", error);
    return true; // Treat as expired if there's any parsing error
  }
};

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Check token expiration
    if (isTokenExpired()) {
      clearSession();
    } else {
      const token = localStorage.getItem("accessToken");
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    console.error("Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// Add response interceptor to handle 401 Unauthorized errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status } = error.response;
        if (status === 401) {
          clearSession();
          showToast("Session expired. Please log in again.", ToastTypes.ERROR);
          
          // Redirect after a short delay
          setTimeout(() => {
            window.location.href = "/login";
          }, 1500);
        } else if (status >= 500) {
        showToast("Server error. Please try again later.", ToastTypes.ERROR);
      } else if (status === 400) {
        // Optionally show a specific message for 400 errors or use the server message
        const errorMessage = error.response.data?.message || "Invalid request. Please check your inputs.";
        showToast(errorMessage, ToastTypes.ERROR);
      }
    } else if (error.request) {
      // Request was made but no response received
      showToast("Network error. Please check your connection.", ToastTypes.ERROR);
    }
    
    return Promise.reject(error);
  }
);

// Helper function to handle API errors during initial loading
export const safeApiCall = async (apiCall, fallbackValue = null) => {
  try {
    const response = await apiCall();
    return response;
  } catch (error) {
    console.error("API call failed:", error);
    return fallbackValue;
  }
};

export default api;